import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { productionPacksAPI, finalAdSetsAPI } from '../lib/api';
import { getCategoryStyle, formatRelativeTime } from '../lib/utils';
import { toast } from 'sonner';
import {
  Package, CheckCircle, Clock, Search, Eye,
  Trash2, Calendar, Check, Image, Film,
  Loader2, Sparkles, ArrowUp, ArrowDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const ProductionPacks = () => {
  const { activeBrand, isFounder, isIdeaManager } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [mainTab, setMainTab] = useState('in-production');
  const [completedTab, setCompletedTab] = useState('not-posted');
  const [completedSort, setCompletedSort] = useState('asc'); // 'asc' (earliest first) | 'desc' | null
  const isFounderUser = isFounder();
  const canDeletePack = isFounderUser || isIdeaManager();

  useEffect(() => {
    if (activeBrand) fetchPacks();
  }, [activeBrand]);

  const fetchPacks = async () => {
    try {
      setLoading(true);
      const response = await productionPacksAPI.getAll({}, activeBrand);
      setPacks(response.data || []);
    } catch { toast.error('Failed to fetch production packs'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (packId) => {
    try {
      await productionPacksAPI.delete(packId);
      toast.success('Pack deleted');
      setDeleteConfirm(null);
      fetchPacks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const handleTogglePosted = async (e, packId) => {
    e.stopPropagation();
    try {
      const res = await productionPacksAPI.markPosted(packId);
      // Switch to the tab where the pack moved to
      if (res.data.posted) {
        setCompletedTab('posted');
      } else {
        setCompletedTab('not-posted');
      }
      fetchPacks();
      toast.success(res.data.posted ? 'Marked as posted' : 'Unmarked as posted');
    } catch { toast.error('Failed to update'); }
  };

  const handleScheduleDate = async (e, packId, date) => {
    e.stopPropagation();
    try {
      await productionPacksAPI.update(packId, { posting_date: date });
      toast.success('Date updated');
      fetchPacks();
    } catch { toast.error('Failed to update'); }
  };

  const handleToggleUseAsAd = async (e, pack) => {
    e.stopPropagation();
    const already = !!pack.used_as_ad;
    if (!already && (!pack.final_assets_media || pack.final_assets_media.length === 0)) {
      toast.error('Upload final assets before using this pack as an ad');
      return;
    }
    try {
      if (already) {
        await finalAdSetsAPI.remove(pack.id);
        toast.success('Removed from Final Ad Sets');
      } else {
        await finalAdSetsAPI.addFromPack(pack.id);
        toast.success('Added to Final Ad Sets');
      }
      fetchPacks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  // Filter packs
  const inProduction = packs.filter(p => p.approval_status !== 'Completed');
  const completed = packs.filter(p => p.approval_status === 'Completed');
  const notPosted = completed.filter(p => !p.posted);
  const posted = completed.filter(p => p.posted);

  const searchFilter = (pack) => {
    if (!searchTerm) return true;
    const title = (pack.idea_title || pack.title || '').toLowerCase();
    const cat = (pack.category || '').toLowerCase();
    return title.includes(searchTerm.toLowerCase()) || cat.includes(searchTerm.toLowerCase());
  };

  const sortByPostingDate = (list) => {
    if (!completedSort) return list;
    const dir = completedSort === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      // Packs without a date sink to the bottom regardless of direction
      const aDate = a.posting_date || '';
      const bDate = b.posting_date || '';
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return dir * aDate.localeCompare(bDate);
    });
  };

  const currentPacks = mainTab === 'in-production'
    ? inProduction.filter(searchFilter)
    : sortByPostingDate((completedTab === 'not-posted' ? notPosted : posted).filter(searchFilter));

  const PackCard = ({ pack, showPostControls }) => {
    const categoryStyle = getCategoryStyle(pack.category);
    const title = pack.idea_title || pack.title || 'Untitled';
    const finalCount = (pack.final_assets_media || []).length;
    const firstFinal = (pack.final_assets_media || [])[0];
    const thumbUrl = firstFinal ? productionPacksAPI.getFinalAssetUrl(pack.id, firstFinal.id) : null;

    return (
      <div
        onClick={() => navigate(`/production-packs/${pack.id}`)}
        className="bg-white border border-zinc-200 rounded-sm hover:border-zinc-300 cursor-pointer transition-all group"
        data-testid={`production-pack-${pack.id}`}
      >
        <div className="flex">
          {/* Thumbnail */}
          {showPostControls && thumbUrl ? (
            <div className="w-20 h-20 shrink-0 bg-zinc-100 overflow-hidden">
              {firstFinal?.is_video ? (
                <video src={thumbUrl} className="w-full h-full object-cover" muted />
              ) : (
                <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
              )}
            </div>
          ) : showPostControls ? (
            <div className="w-20 h-20 shrink-0 bg-zinc-50 flex items-center justify-center">
              <Film className="w-6 h-6 text-zinc-300" />
            </div>
          ) : null}

          {/* Content */}
          <div className="flex-1 p-4 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-zinc-900 truncate group-hover:text-zinc-700">{title}</h3>
                <div className="flex items-center gap-2 mt-1">
                  {pack.category && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${categoryStyle.badge}`}>
                      {pack.category}
                    </span>
                  )}
                  {!showPostControls && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      pack.approval_status === 'Approved' ? 'bg-emerald-50 text-emerald-700' :
                      pack.approval_status === 'Pending' ? 'bg-amber-50 text-amber-700' :
                      'bg-zinc-100 text-zinc-600'
                    }`}>
                      {pack.approval_status}
                    </span>
                  )}
                  {isFounderUser && pack.creator_name && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100"
                      title={pack.creator_email || pack.creator_name}
                      data-testid={`pack-creator-badge-${pack.id}`}
                    >
                      by {pack.creator_name}
                    </span>
                  )}
                  {finalCount > 0 && (
                    <span className="text-xs text-zinc-400 flex items-center gap-1">
                      <Film size={10} /> {finalCount} final
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                {showPostControls && (
                  <>
                    <input
                      type="date"
                      value={pack.posting_date?.split('T')[0] || ''}
                      onChange={(e) => handleScheduleDate(e, pack.id, e.target.value)}
                      className="text-xs border border-zinc-200 rounded px-2 py-1 text-zinc-600 w-32"
                      title="Schedule date"
                      data-testid={`schedule-date-${pack.id}`}
                    />
                    <button
                      onClick={(e) => handleTogglePosted(e, pack.id)}
                      className={`p-1.5 rounded transition-colors ${
                        pack.posted
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-zinc-100 text-zinc-400 hover:text-zinc-600'
                      }`}
                      title={pack.posted ? 'Posted' : 'Mark as posted'}
                      data-testid={`toggle-posted-${pack.id}`}
                    >
                      <Check size={14} />
                    </button>
                    <button
                      onClick={(e) => handleToggleUseAsAd(e, pack)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                        pack.used_as_ad
                          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                          : 'bg-zinc-900 text-white hover:bg-zinc-800'
                      }`}
                      title={pack.used_as_ad ? 'Click to remove from Final Ad Sets' : 'Add to Final Ad Sets'}
                      data-testid={`use-as-ad-${pack.id}`}
                    >
                      <Sparkles size={12} />
                      {pack.used_as_ad ? 'In Ad Sets' : 'Use as Ad'}
                    </button>
                  </>
                )}
                {canDeletePack && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(pack); }}
                    className="p-1.5 text-zinc-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                    data-testid={`delete-production-pack-${pack.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
              {pack.posting_date && (
                <span className="flex items-center gap-1">
                  <Calendar size={10} /> {new Date(pack.posting_date).toLocaleDateString()}
                </span>
              )}
              <span>{formatRelativeTime(pack.updated_at)}</span>
              {pack.post_type && <span className="px-1.5 py-0.5 bg-zinc-100 rounded">{pack.post_type}</span>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="production-packs-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Production</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage content from production through posting</p>
      </div>

      {/* Main Tabs */}
      <div className="flex items-center gap-6 border-b border-zinc-200">
        <button
          onClick={() => setMainTab('in-production')}
          className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
            mainTab === 'in-production'
              ? 'border-zinc-900 text-zinc-900'
              : 'border-transparent text-zinc-400 hover:text-zinc-600'
          }`}
          data-testid="tab-in-production"
        >
          <Package className="w-4 h-4 inline mr-2" />
          In Production
          <span className="ml-2 text-xs px-1.5 py-0.5 bg-zinc-100 rounded-full">{inProduction.length}</span>
        </button>
        <button
          onClick={() => setMainTab('completed')}
          className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
            mainTab === 'completed'
              ? 'border-zinc-900 text-zinc-900'
              : 'border-transparent text-zinc-400 hover:text-zinc-600'
          }`}
          data-testid="tab-completed"
        >
          <CheckCircle className="w-4 h-4 inline mr-2" />
          Completed
          <span className="ml-2 text-xs px-1.5 py-0.5 bg-zinc-100 rounded-full">{completed.length}</span>
        </button>
      </div>

      {/* Sub-tabs for Completed */}
      {mainTab === 'completed' && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCompletedTab('not-posted')}
              className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors ${
                completedTab === 'not-posted'
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              data-testid="subtab-not-posted"
            >
              Not Posted ({notPosted.length})
            </button>
            <button
              onClick={() => setCompletedTab('posted')}
              className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors ${
                completedTab === 'posted'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              data-testid="subtab-posted"
            >
              Posted ({posted.length})
            </button>
          </div>

          {/* Date sort */}
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-zinc-400">Sort by date:</span>
            <button
              onClick={() => setCompletedSort('asc')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full transition-colors ${
                completedSort === 'asc'
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              title="Earliest first"
              data-testid="sort-date-asc"
            >
              <ArrowUp size={11} /> Earliest
            </button>
            <button
              onClick={() => setCompletedSort('desc')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full transition-colors ${
                completedSort === 'desc'
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              title="Latest first"
              data-testid="sort-date-desc"
            >
              <ArrowDown size={11} /> Latest
            </button>
            <button
              onClick={() => setCompletedSort(null)}
              className={`px-2.5 py-1 rounded-full transition-colors ${
                completedSort === null
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              title="No date sort"
              data-testid="sort-date-off"
            >
              Default
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search packs..."
          className="pl-10 border-zinc-200"
          data-testid="search-packs"
        />
      </div>

      {/* Pack List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      ) : currentPacks.length === 0 ? (
        <div className="text-center py-16 bg-white border border-zinc-200 rounded-sm">
          <Package className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500 text-sm">
            {mainTab === 'in-production' ? 'No packs in production' :
              completedTab === 'not-posted' ? 'No completed packs awaiting posting' :
              'No posted packs yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {currentPacks.map(pack => (
            <PackCard
              key={pack.id}
              pack={pack}
              showPostControls={mainTab === 'completed'}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Production Pack</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure? This will permanently delete "{deleteConfirm?.idea_title || deleteConfirm?.title}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm?.id)} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductionPacks;
